import {createFeatureSelector, createSelector} from '@ngrx/store';
import {Aktion, Aufgabe, Checkliste, Dokument, FunktionsbereichRolle, InfoKategorie, Rolle, RolleBenutzer, Veranstaltung, VeranstaltungDetail} from '../../../clients/veranstaltungen';
import {ListInputItem} from '../../shared/sopi-list/sopi-list.model';
import {ModuleState, PageState} from './veranstaltungen.reducer';

/**
 * The createFeatureSelector function selects a piece of state from the root of the state object.
 * This is used for selecting feature states that are loaded eagerly or lazily.
 */
export const getModuleState = createFeatureSelector<ModuleState>('VeranstaltungenPage');

export const getPageState = createSelector(getModuleState, (state: ModuleState) => state.page);

// DATA
export const getVeranstaltungBerechtigungen = createSelector(getPageState, (state: PageState) => state.veranstaltungBerechtigungen);
export const getAngelegteVeranstaltungenListe = createSelector(getPageState, (state: PageState) => state.angelegteVeranstaltungenListe);
export const getVeroeffentlichteVeranstaltungenListe = createSelector(getPageState, (state: PageState) => state.veroeffentlichteVeranstaltungenListe);
export const getBeendeteVeranstaltungenListe = createSelector(getPageState, (state: PageState) => state.beendeteVeranstaltungenListe);
export const getVeranstaltungenDetail = createSelector(getPageState, (state: PageState) => state.veranstaltungenDetail);
export const getAktiveVeranstaltungen = createSelector(getAngelegteVeranstaltungenListe, getVeroeffentlichteVeranstaltungenListe, (angelegte: Veranstaltung[], veroeffentlichte: Veranstaltung[]) => {

    let angelegteMap = [<ListInputItem>{
        groupId: 'Angelegte Veranstaltungen',
        groupTitle: 'Angelegte Veranstaltungen',
        itemId: null,
        itemTitle: null,
        item: null
    }];

    if (angelegte && angelegte.length > 0) {
        angelegteMap = angelegte.map((item: Veranstaltung) => <ListInputItem>{
            groupId: 'Angelegte Veranstaltungen',
            groupTitle: 'Angelegte Veranstaltungen',
            itemId: item.veranstaltung_id,
            itemTitle: item.titel,
            item: item
        });
    }


    let veroeffentlichteMap = [<ListInputItem>{
        groupId: 'Veroeffentlichte Veranstaltungen',
        groupTitle: 'Veröffentlichte Veranstaltungen',
        itemId: null,
        itemTitle: null,
        item: null
    }];

    if (veroeffentlichte && veroeffentlichte.length > 0) {
        veroeffentlichteMap = veroeffentlichte.map((item: Veranstaltung) => <ListInputItem>{
            groupId: 'Veroeffentlichte Veranstaltungen',
            groupTitle: 'Veröffentlichte Veranstaltungen',
            itemId: item.veranstaltung_id,
            itemTitle: item.titel,
            item: item
        });
    }


    return angelegteMap.concat(veroeffentlichteMap);
});

export const getArchivierteVeranstaltungen = createSelector(getBeendeteVeranstaltungenListe, (beendete: Veranstaltung[]) => {

    let beendeteMap = [<ListInputItem>{
        groupId: 'Beendete Veranstaltungen',
        groupTitle: 'Beendete Veranstaltungen',
        itemId: null,
        itemTitle: null,
        item: null
    }];

    if (beendete && beendete.length > 0) {
        beendeteMap = beendete.map((item: Veranstaltung) => <ListInputItem>{
            groupId: 'Beendete Veranstaltungen',
            groupTitle: 'Beendete Veranstaltungen',
            itemId: item.veranstaltung_id,
            itemTitle: item.titel,
            item: item
        });
    }

    return beendeteMap;
});


export const getDynamischeVeranstaltungFelder = createSelector(getVeranstaltungenDetail, (detail: VeranstaltungDetail): { [key: string]: string; } => {
    const felder: { [key: string]: string; } = {};

    detail.kopfdaten.forEach(x => {
        if (x.feld_typ === 'FELD') {
            felder[x.key] = x.wert;
        } else if (x.feld_typ === 'GRUPPE') {
            x.children.forEach(y => {
                if (y.feld_typ === 'FELD') {
                    felder[y.key] = y.wert;
                }
            });
        }
    });

    return felder;
});

export const getFunktionsbereicheListe = createSelector(getPageState, (state: PageState) => {
    const funktionsbereicheListe = [...state.funktionsbereicheListe];
    return funktionsbereicheListe.sort(function (a, b) {
        if (a.titel.toLowerCase() < b.titel.toLowerCase()) {
            return -1;
        }
        if (a.titel.toLowerCase() > b.titel.toLowerCase()) {
            return 1;
        }
        return 0;
    });
});
export const getFunktionsbereicheChecklistenListe = createSelector(getPageState, (state: PageState) => {
    const liste = [...state.funktionsbereicheChecklistenListe];

    const retListe: Array<ListInputItem> = [];
    liste.forEach((fbCheckliste) => {
        fbCheckliste.checklisten.forEach((item: Checkliste) => {
            retListe.push({
                groupId: fbCheckliste.funktionsbereich.funktionsbereich_id,
                groupTitle: fbCheckliste.funktionsbereich.titel,
                itemId: item.checkliste_id,
                itemTitle: item.titel,
                item: item
            });
        });
    });

    return retListe;
});

export const getChecklistenListe = createSelector(getPageState, (state: PageState) => {
    const checklistenListe = [...state.checklistenListe];
    return checklistenListe.sort(function (a, b) {
        if (a.titel.toLowerCase() < b.titel.toLowerCase()) {
            return -1;
        }
        if (a.titel.toLowerCase() > b.titel.toLowerCase()) {
            return 1;
        }
        return 0;
    }).sort((a, b) => {
        if (a.reihenfolge < b.reihenfolge) {
            return -1;
        }
        if (a.reihenfolge > b.reihenfolge) {
            return 1;
        }
        return 0;
    });
});
export const getAufgabenListe = createSelector(getPageState, (state: PageState) => {
    const aufgabenListe = [...state.aufgabenListe];
    return aufgabenListe
        /*.sort((a, b) => {
        if (a && a.titel && b && b.titel && a.titel.toLowerCase() < b.titel.toLowerCase()) {
            return -1;
        }
        if (a && a.titel && b && b.titel && a.titel.toLowerCase() > b.titel.toLowerCase()) {
            return 1;
        }
        return 0;
    }).sort((a, b) => {
        if (a.reihenfolge < b.reihenfolge) {
            return -1;
        }
        if (a.reihenfolge > b.reihenfolge) {
            return 1;
        }
        return 0;
    })*/
        .map((item: Aufgabe) => <ListInputItem>{
            groupId: '',
            groupTitle: '',
            itemId: item.aufgabe_id,
            itemTitle: item.titel,
            item: item
        });
});

export const getSelectedAufgabe = createSelector(getPageState, (state: PageState) => state.selectedAufgabe);

export const getUiFunktionsbereicheRollen = createSelector(getPageState, (state: PageState) => {
    const res = [];
    state.funktionsbereicheRollenListe.forEach((fb) => {
        fb.rollen.forEach((rolle) => {
            res.push(<ListInputItem>{
                groupId: fb.funktionsbereich.funktionsbereich_id,
                groupTitle: fb.funktionsbereich.titel,
                group: fb.funktionsbereich,
                itemId: rolle.rolle_id,
                itemTitle: rolle.titel,
                item: {funktionsbereich: fb.funktionsbereich, rolle: rolle, berechtigung: fb.berechtigung},
            });
        });
    });
    return res;
});

export const getFunktionsbereicheRollenAlleListe = createSelector(getPageState, (state: PageState) => {
    const funktionsbereicheRollenAlleListe = [...state.funktionsbereicheRollenAlleListe];
    return funktionsbereicheRollenAlleListe.sort(function (a, b) {
        if (a.funktionsbereich.titel.toLowerCase() < b.funktionsbereich.titel.toLowerCase()) {
            return -1;
        }
        if (a.funktionsbereich.titel.toLowerCase() > b.funktionsbereich.titel.toLowerCase()) {
            return 1;
        }
        return 0;
    });
});
export const getFunktionsbereicheRollenListeLaden = createSelector(getPageState, (state: PageState) => state.uiZeigeFunktionsbereicheRollenLadeanimation);
export const getVeranstaltungRollenListe = createSelector(getPageState, (state: PageState) => {
    const veranstaltungRollenListe = [...state.veranstaltungRollenListe];
    return veranstaltungRollenListe.sort(function (a, b) {
        if (a.titel.toLowerCase() < b.titel.toLowerCase()) {
            return -1;
        }
        if (a.titel.toLowerCase() > b.titel.toLowerCase()) {
            return 1;
        }
        return 0;
    });
});
export const getRolleBenutzerListe = createSelector(getPageState, (state: PageState) => {
    const rolleBenutzerListe = [...state.rolleBenutzerListe];
    return rolleBenutzerListe.sort(function (a, b) {
        if (a.benutzer_name.toLowerCase() < b.benutzer_name.toLowerCase()) {
            return -1;
        }
        if (a.benutzer_name.toLowerCase() > b.benutzer_name.toLowerCase()) {
            return 1;
        }
        return 0;
    });
});
export const getBenutzerverwaltungRolleBenutzerListe = createSelector(getPageState, (state: PageState) => state.benutzerverwaltungRolleBenutzerListe);
export const getUiBenutzerverwaltungRolleBenutzerListe = createSelector(getPageState, getRolleBenutzerListe, (state: PageState, rolleBenutzer: RolleBenutzer[]) => {
    if (!state.benutzerverwaltungRolleBenutzerListe) {
        return [];
    }
    return state.benutzerverwaltungRolleBenutzerListe
        .filter((item) => rolleBenutzer.findIndex(rb => rb.benutzer_id === item.benutzer_id) === -1)
        .map(item => <ListInputItem>{
            groupTitle: item.benutzer_name.substr(0, 1).toUpperCase(),
            groupId: item.benutzer_name.substr(0, 1).toUpperCase(),
            itemTitle: item.benutzer_name,
            itemId: item.benutzer_id,
            item: item
        });
});
// export const getBenutzerverwaltungRolleBenutzer = createSelector(getPageState, (state: PageState) => state.benutzerverwaltungRolleBenutzer);
export const getFreigabeRollenListe = createSelector(getPageState, (state: PageState) => state.freigabeRollenListe);
export const getLeseRollenListe = createSelector(getPageState, (state: PageState) => state.leseRollenListe);
export const getAbarbeiterRollenListe = createSelector(getPageState, (state: PageState) => state.abarbeiterRollenListe);
export const getDokumenteListe = createSelector(getPageState, (state: PageState) => {
    const liste = [...state.dokumenteListe.filter(x => !x.ist_allgemeines_dokument)];

    return liste.map((item: Dokument) => <ListInputItem>{
        groupId: item.gruppierung,
        groupTitle: item.gruppierung,
        itemId: item.dokument_id,
        itemTitle: item.titel,
        item: item
    });
});

export const getAllgemeineDokumenteListe = createSelector(getPageState, (state: PageState) => {
    const liste = [...state.dokumenteListe.filter(x => x.ist_allgemeines_dokument)];

    return liste.map((item: Dokument) => <ListInputItem>{
        groupId: item.gruppierung,
        groupTitle: item.gruppierung,
        itemId: item.dokument_id,
        itemTitle: item.titel,
        item: item
    });
});
export const getAnzahlAllgemeineDokumente = createSelector(getAllgemeineDokumenteListe, (liste: Array<ListInputItem>) => liste.length);
export const getAnzahlSpieltagDokumente = createSelector(getDokumenteListe, (liste: Array<ListInputItem>) => liste.length);

export const getDokumenteLadeanimation = createSelector(getPageState, (state: PageState) => state.uiZeigeDokumenteLadeanimation);

export const getAktuelleRolle = createSelector(getPageState, (state: PageState) => state.aktuelleRolle);
export const getVeranstaltungStatus = createSelector(getPageState, (state: PageState) => state.veranstaltungStatus);



// UI
export const getUiAngelegteVeranstaltungenListeLadeanimation = createSelector(getPageState, (state: PageState) => state.uiAngelegteVeranstaltungenListeLadeanimation);
export const getUiVeroeffentlichteVeranstaltungenListeLadeanimation = createSelector(getPageState, (state: PageState) => state.uiVeroeffentlichteVeranstaltungenListeLadeanimation);
export const getUiBeendeteVeranstaltungenListeLadeanimation = createSelector(getPageState, (state: PageState) => state.uiBeendeteVeranstaltungenListeLadeanimation);
export const getUiZeigeDetailbereich = createSelector(getPageState, (state: PageState) => state.uiZeigeDetailbereich);
export const getUiZeigeVeranstaltungenDetailLadeanimation = createSelector(getPageState, (state: PageState) => state.uiZeigeVeranstaltungenDetailLadeanimation);
export const getUiZeigeFunktionsbereicheLadeanimation = createSelector(getPageState, (state: PageState) => state.uiZeigeFunktionsbereicheLadeanimation);
export const getUiZeigeChecklistenLadeanimation = createSelector(getPageState, (state: PageState) => state.uiZeigeChecklistenLadeanimation);
export const getUiZeigeAufgabenLadeanimation = createSelector(getPageState, (state: PageState) => state.uiZeigeAufgabenLadeanimation);
export const getUiZeigeAufgabenFehler = createSelector(getPageState, (state: PageState) => state.uiZeigeAufgabenFehler);
export const getUiZeigeBenutzerverwaltungRolleBenutzer = createSelector(getPageState, (state: PageState) => state.uiZeigeBenutzerverwaltungRolleBenutzerLadeanimation);
export const getUiZeigeLeseRollenLadeanimation = createSelector(getPageState, (state: PageState) => state.uiZeigeLeseRollenLadeanimation);
export const getUiZeigeFreigabeRollenLadeanimation = createSelector(getPageState, (state: PageState) => state.uiZeigeFreigabeRollenLadeanimation);
export const getUiZeigeAbarbeiterRollenLadeanimation = createSelector(getPageState, (state: PageState) => state.uiZeigeAbarbeiterRollenLadeanimation);
export const getUiZeigeVeranstaltungLoeschen = createSelector(getPageState, (state: PageState) => state.uiZeigeVeranstaltungLoeschen);
export const getUiZeigeNeueVeranstaltung = createSelector(getPageState, (state: PageState) => state.uiZeigeNeueVeranstaltung);
export const getUiZeigeFunktionsbereicheChecklistenLaden = createSelector(getPageState, (state: PageState) => state.uiZeigeFunktionsbereicheChecklistenLaden);
export const getUiZeigeFunktionsbereicheChecklistenFehler = createSelector(getPageState, (state: PageState) => state.uiZeigeFunktionsbereicheChecklistenFehler);
export const getUiSelektiertFunktionsbereicheCheckliste = createSelector(getPageState, (state: PageState) => state.uiSelektiertFunktionsbereicheCheckliste);
export const getUiRollenBenutzerSelektiert = createSelector(getPageState, (state: PageState) => state.uiRollenBenutzerSelektiert);

export const getDokumentSelected = createSelector(getPageState, (state: PageState) => state.dokumentSelected);

export const getDokumentBerechtigung = createSelector(getPageState, (state: PageState) => state.dokumentBerechtigung);
export const getDokumentBerechtigungLaden = createSelector(getPageState, (state: PageState) => state.dokumentBerechtigungLaden);
export const getDokumentBerechtigungFehler = createSelector(getPageState, (state: PageState) => state.dokumentBerechtigungFehler);

export const getLeseBerechtigungRollen = createSelector(getDokumentSelected, (dokument: Dokument) => {
    let liste: Array<FunktionsbereichRolle> = [];
    if (dokument && dokument.dokument_id !== null && dokument.berechtigung_rollen !== null) {
        liste = dokument.berechtigung_rollen2;
    }
    if (liste === null || liste === undefined || liste.length === 0) {
        return [];
    }

    return liste.map((item: FunktionsbereichRolle) => <ListInputItem>{
        groupId: '',
        groupTitle: '',
        itemId: item.rolle.rolle_id,
        itemTitle: item.rolle.titel,
        item: item
    });
});

export const getBearbeitenBerechtigungRollen = createSelector(getDokumentSelected, (dokument: Dokument) => {
    let liste: Array<FunktionsbereichRolle> = [];
    if (dokument && dokument.dokument_id !== null && dokument.bearbeiten_berechtigung_rollen !== null) {
        liste = dokument.bearbeiten_berechtigung_rollen2;
    }

    if (liste === null || liste === undefined || liste.length === 0) {
        return [];
    }

    return liste.map((item: FunktionsbereichRolle) => <ListInputItem>{
        groupId: '',
        groupTitle: '',
        itemId: item.rolle.rolle_id,
        itemTitle: item.rolle.titel,
        item: item
    });
});

export const getDokumentLeseBerechtigungenSelected = createSelector(getPageState, (state: PageState) => state.dokumentLeseBerechtigungenSelected);
export const getDokumentBearbeitenBerechtigungenSelected = createSelector(getPageState, (state: PageState) => state.dokumentBearbeitungBerechtigungenSelected);

export const getPandemieLevelStichtag = createSelector(getPageState, (state: PageState): Date => state.pandemieLevelStichtag);
export const getPandemieCheck = createSelector(getPageState, (state: PageState) => state.pandemieCheck);
export const getSelectedPandemieLevelId = createSelector(getPageState, (state: PageState) => state.selectedPandemieLevelId);

export const getVeranstaltungPandemieInfo = createSelector(getPageState, (state: PageState) => state.veranstaltungPandemieInfo);
export const getVeranstaltungPandemieInfoLaden = createSelector(getPageState, (state: PageState) => state.veranstaltungPandemieInfoLaden);
export const getVeranstaltungPandemieInfoFehler = createSelector(getPageState, (state: PageState) => state.veranstaltungPandemieInfoFehler);

export const getFunktionsbereiche = createSelector(getPageState, (state: PageState) => {
    const res: Array<ListInputItem> = [];
    state.funktionsbereicheListe.forEach((fb) => {
        res.push(<ListInputItem>{
            groupId: '',
            groupTitle: '',
            group: null,
            itemId: fb.funktionsbereich_id,
            itemTitle: fb.titel,
            item: fb
        });
    });
    return res;
});

export const getFunktionsbereicheLaden = createSelector(getPageState, (state: PageState) => state.uiZeigeFunktionsbereicheLadeanimation);
export const getFunktionsbereicheFehler = createSelector(getPageState, (state: PageState) => state.uiZeigeFunktionsbereicheFehler);

export const getChecklistenAufgaben = createSelector(getPageState, (state: PageState) => {
    const res: Array<ListInputItem> = [];
    state.checklistenAufgaben.forEach((cl) => {
        cl.aufgaben.forEach(a => {
            let title = a.titel;
            if (a.berechtigung_benutzer_liste !== null && a.berechtigung_benutzer_liste.length > 0) {
                a.berechtigung_benutzer_liste.forEach(x => title = title + ' ' + x.benutzer.vorname + ' ' + x.benutzer.nachname);
            }
            res.push(<ListInputItem>{
                groupId: cl.checkliste.checkliste_id,
                groupTitle: cl.checkliste.titel,
                group: cl,
                groupClass: 'funktionsbereiche',
                itemId: a.aufgabe_id,
                itemTitle: title,
                item: a
            });
        });
    });
    return res;
});

export const getSelectedFunktionsbereichId = createSelector(getPageState, (state: PageState) => state.selectedFunktionsbereichId);

export const getChecklistenAufgabenLaden = createSelector(getPageState, (state: PageState) => state.checklistenAufgabenLaden);
export const getChecklistenAufgabenFehler = createSelector(getPageState, (state: PageState) => state.checklistenAufgabenFehler);

export const getSelectedAufgaben = createSelector(getPageState, (state: PageState) => state.selectedAufgaben);

